import React, { useEffect, useMemo } from "react";
import styled from "styled-components";
import { Typography } from "@shared/ui/Typography";
import { Button } from "@shared/ui/Button";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "next-i18next";
import {
  CreditTypeEnum,
  IApplicationForm,
} from "@entities/Product/Application/types";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  applicationValidationSchema,
  findOption,
} from "@entities/Product/Application/utils";
import {
  $applicationFormFinalStep,
  submitApplicationFinalStep,
} from "@entities/Product/Application/model";
import { Step2Fields } from "@entities/Product/Application/ui/Step2Fields";
import { Step3Fields } from "@entities/Product/Application/ui/Step3Fields";
import { Step4Fields } from "@entities/Product/Application/ui/Step4Fields";
import { Step5Fields } from "@entities/Product/Application/ui/Step5Fields";
import { useUnit } from "effector-react";
import {
  calcMonthlyPaid,
  extractNumbers,
  setSpacesInNumber,
} from "@shared/lib";

interface IProps {
  cities: { value: string; label: string }[];
  creditTypeOptions: { value: string; label: string }[];
  periodOptions: { value: string; label: string }[];
  creditAimOptions: { value: string; label: string }[];
  classificationOptions: { value: string; label: string }[];
  pledgeTypes: { value: string; label: string }[];
  stateOptions: { value: string; label: string }[];
  carBrandOptions: { value: string; label: string }[];
}

export function ApplicationFinalStep({
  cities,
  periodOptions,
  creditAimOptions,
  classificationOptions,
  pledgeTypes,
  creditTypeOptions,
  stateOptions,
  carBrandOptions,
}: IProps) {
  const { t } = useTranslation();
  const [finalStep, submitStep] = useUnit([
    $applicationFormFinalStep,
    submitApplicationFinalStep,
  ]);

  const methods = useForm<IApplicationForm>({
    mode: "onChange",
    shouldUnregister: false,
    resolver: zodResolver(
      applicationValidationSchema(finalStep.credit_type as CreditTypeEnum, t)
    ),
    defaultValues: finalStep,
  });

  const onSubmit = methods.handleSubmit(async (data) => {
    submitStep(data);
  });

  useEffect(() => {
    const { credit_type, credit_period, credit_sum } = methods.getValues();

    if (credit_type && credit_period && credit_sum) {
      const creditOption = findOption(creditTypeOptions, credit_type);
      const periodOption = findOption(periodOptions, credit_period);
      const period = extractNumbers(periodOption.label);
      const rate = creditOption.loan_rate;
      const sum = extractNumbers(credit_sum);

      methods.setValue(
        "credit_monthly_payment",
        String(setSpacesInNumber(Math.ceil(calcMonthlyPaid(period, sum, rate))))
      );
    }
  }, [
    methods.watch("credit_type"),
    methods.watch("credit_period"),
    methods.watch("credit_sum"),
  ]);

  const hasCollateral = useMemo(
    () =>
      [CreditTypeEnum.PLEDGE_REAL_ESTATE, CreditTypeEnum.PLEDGE_AUTO].includes(
        methods.watch("credit_type") as CreditTypeEnum
      ),
    [methods.watch("credit_type")]
  );

  return (
    <>
      <StyledSubtitle tag="p" color="primary_light_gray">
        {t(
          "Проверьте ваши данные, и отредактируйте при необходимости, перед тем как отправить на рассмотрение."
        )}
      </StyledSubtitle>
      <StyledWrapper onSubmit={onSubmit}>
        <FormProvider {...methods}>
          <StyledSection>
            <StyledSectionTitle fontSize="1.8rem" weight={700}>
              {t("Персональные данные")}
            </StyledSectionTitle>
            <StyledSectionContent>
              <Step2Fields cities={cities} />
            </StyledSectionContent>
          </StyledSection>
          <StyledSection>
            <StyledSectionTitle fontSize="1.8rem" weight={700}>
              {t("Кредитная информация")}
            </StyledSectionTitle>
            <StyledSectionContent>
              <Step3Fields
                periodOptions={periodOptions}
                creditAimOptions={creditAimOptions}
                creditTypeOptions={creditTypeOptions}
                carBrandOptions={carBrandOptions}
              />
            </StyledSectionContent>
          </StyledSection>
          <StyledSection>
            <StyledSectionTitle fontSize="1.8rem" weight={700}>
              {t("Информация о работе")}
            </StyledSectionTitle>
            <StyledSectionContent>
              <Step4Fields classificationOptions={classificationOptions} />
            </StyledSectionContent>
          </StyledSection>
          {hasCollateral && (
            <StyledSection>
              <StyledSectionTitle fontSize="1.8rem" weight={700}>
                {t("Информация о залоге")}
              </StyledSectionTitle>
              <StyledSectionContent>
                <Step5Fields
                  pledgeTypes={pledgeTypes}
                  states={stateOptions}
                  brandOptions={carBrandOptions}
                  productId={finalStep.credit_type}
                />
              </StyledSectionContent>
            </StyledSection>
          )}
        </FormProvider>
        <StyledBottom>
          <div />
          <Button type="submit" disabled={!methods.formState.isValid}>
            {t(`Подтвердить`)}
          </Button>
        </StyledBottom>
      </StyledWrapper>
    </>
  );
}

const StyledSubtitle = styled(Typography)`
  margin-bottom: 6.4rem;
  max-width: 57.6rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    margin-top: -2rem;
    margin-bottom: 4rem;
  }
`;

const StyledWrapper = styled.form`
  display: grid;
  grid-gap: 6.4rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 3.2rem;
  }
`;

const StyledBottom = styled.div`
  display: grid;
  margin-top: 1.6rem;
  grid-template-columns: 1.2fr 1.8fr;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-template-columns: 1fr;
    grid-gap: 3.2rem;
  }
`;

const StyledSection = styled.section`
  display: grid;
  grid-gap: 2.4rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 1.6rem;
  }
`;

const StyledSectionTitle = styled(Typography)`
  display: grid;
  grid-gap: 2.4rem;
  color: #67707a;
`;

const StyledSectionContent = styled.div`
  display: grid;
  grid-gap: 2.4rem;

  @media screen and (max-width: ${(props) => props.theme.sizes.tabletMin}) {
    grid-gap: 1.6rem;
  }
`;
